import { useUser } from "@/components/UserProvider";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import type { Matter, MedicalTreatment } from "@/lib/types";
import { ServerErrorFallbackCard } from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { cn } from "@/lib/utils/cn";
import { formatUsersName } from "@/lib/utils/format";
import { ErrorBoundary } from "@sentry/react";
import { Link } from "@tanstack/react-router";
import { Calendar, ChevronRight, Clock3, FileText } from "lucide-react";
import dayjs from "dayjs";
import { getTreatmentAppointmentStartsAt } from "@/lib/utils/matters/medical-treatment-appointment-dates";
import {
  formatCurrency,
  formatDateWithRelativeDays,
  getLatestByDate,
  getProviderName,
} from "../matter-overview-utils";

type ProviderCardProps = {
  matter: Matter;
  treatment: MedicalTreatment;
};

function ProviderCardDangerous({ matter, treatment }: ProviderCardProps) {
  const { hasPagePermission } = useUser();
  const canViewTreatments = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailMedicalTreatments,
  );
  const providerName = getProviderName(matter, treatment);
  const appointments = matter.medicalTreatmentAppointments.filter(
    (appointment) => appointment.medicalTreatmentId === treatment.id,
  );
  const attendedAppointments = appointments.filter((appointment) => {
    if (appointment.outcome === "missed" || appointment.outcome === "scheduled")
      return false;
    const startsAt = getTreatmentAppointmentStartsAt(appointment);
    return startsAt.isValid() && !startsAt.isAfter(dayjs());
  });
  const lastVisit = getLatestByDate(
    attendedAppointments,
    (appointment) => appointment.date,
  );
  const records = matter.medicalTreatmentRecords.filter(
    (record) => record.medicalTreatmentId === treatment.id,
  );
  const receivedRecords = records.filter(
    (record) => record.status === "Received",
  );
  const lastRecord = getLatestByDate(
    receivedRecords,
    (record) => record.receivedDate,
  );
  const lastRecordLabel = lastRecord?.receivedDate
    ? formatDateWithRelativeDays(lastRecord.receivedDate)
    : receivedRecords.length > 0
      ? "Received (date not recorded)"
      : "—";
  const bills = matter.medicalTreatmentBills.filter(
    (bill) => bill.medicalTreatmentId === treatment.id,
  );
  const outstandingRecords = treatment.recordNotApplicable
    ? 0
    : records.filter((record) => !["Received", "N/A"].includes(record.status))
        .length;
  const balance = bills.reduce((sum, bill) => {
    const amount =
      bill.remainingBalance ??
      bill.negotiatedAmount ??
      bill.adjustedBill ??
      bill.originalBill ??
      0;
    return sum + Number(amount);
  }, 0);
  const balanceConfirmed = matter.medicalTreatmentBalanceConfirmations
    .filter((confirmation) => confirmation.medicalTreatmentId === treatment.id)
    .some((confirmation) => confirmation.confirmed);
  const latestBalanceConfirmation = matter.medicalTreatmentBalanceConfirmations
    .filter((confirmation) => confirmation.medicalTreatmentId === treatment.id)
    .slice()
    .sort((a, b) => {
      const aDate = a.date ?? a.dateCreated;
      const bDate = b.date ?? b.dateCreated;
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return dayjs(bDate).diff(dayjs(aDate));
    })
    .at(0);
  const latestConfirmationId = latestBalanceConfirmation?.id;
  const confirmedByConnection = latestConfirmationId
    ? matter.userConnections
        .filter(
          (connection) =>
            connection.medicalTreatmentBalanceConfirmationId ===
            latestConfirmationId,
        )
        .sort((a, b) => dayjs(b.dateCreated).diff(dayjs(a.dateCreated)))
        .at(0)
    : undefined;
  const confirmedByName = formatUsersName(confirmedByConnection)?.trim();
  const balanceConfirmationMeta =
    latestBalanceConfirmation && latestBalanceConfirmation.confirmed
      ? [
          latestBalanceConfirmation.date
            ? formatDateWithRelativeDays(latestBalanceConfirmation.date)
            : undefined,
          confirmedByName ? `by ${confirmedByName}` : undefined,
        ]
          .filter(Boolean)
          .join(" ")
      : undefined;
  const balanceChip = (
    <span
      className={cn(
        "rounded-xl px-2 py-0.5 text-[11px] font-medium",
        balanceConfirmed
          ? "bg-emerald-100 text-emerald-700"
          : "bg-amber-100 text-amber-700",
      )}
    >
      {balanceConfirmed ? "Confirmed" : "Unconfirmed"}
    </span>
  );
  const treatmentTypes = treatment.type;
  const visibleTreatmentTypes = treatmentTypes.slice(0, 2);
  const hiddenTreatmentTypes = treatmentTypes.slice(2);
  const treatmentTypeBadgeClassName =
    "border-border/80 text-foreground inline-flex rounded-xl border px-2.5 py-1 text-[11px]";

  return (
    <div className="border-border/80 bg-card rounded-[20px] border px-4.5 py-4">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-foreground text-[15px] font-semibold">
                {providerName}
              </p>
              {visibleTreatmentTypes.map((type, index) => (
                <span
                  key={`${type}-${index}`}
                  className={treatmentTypeBadgeClassName}
                >
                  {type}
                </span>
              ))}
              {hiddenTreatmentTypes.length > 0 ? (
                <TooltipWrapper message={hiddenTreatmentTypes.join(", ")}>
                  <span className={treatmentTypeBadgeClassName}>
                    +{hiddenTreatmentTypes.length}
                  </span>
                </TooltipWrapper>
              ) : null}
            </div>
          </div>

          {canViewTreatments ? (
            <Link
              to="/matters/$matterId/medical-treatments"
              params={{ matterId: matter.id }}
              className="text-muted-foreground hover:text-foreground inline-flex shrink-0 items-center gap-1 self-start text-sm whitespace-nowrap"
            >
              Go to treatment
              <ChevronRight className="h-4 w-4" />
            </Link>
          ) : null}
        </div>

        <div className="grid min-w-0 gap-x-8 gap-y-2 text-sm sm:grid-cols-2 xl:grid-cols-3">
          <div className="text-foreground flex min-w-0 items-start gap-2">
            <Calendar className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0 break-words">{`Appointments: ${appointments.length}`}</span>
          </div>
          <div className="text-foreground flex min-w-0 items-start gap-2">
            <Clock3 className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0 break-words">{`Last visit: ${formatDateWithRelativeDays(
              lastVisit?.date,
            )}`}</span>
          </div>
          <div className="text-foreground flex min-w-0 items-start gap-2">
            <FileText className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0 break-words">{`Last record: ${lastRecordLabel}`}</span>
          </div>
          <div className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span>Outstanding recs:</span>
            <span
              className={cn(
                "font-medium",
                outstandingRecords > 0 ? "text-orange-600" : "text-emerald-600",
              )}
            >
              {outstandingRecords > 0
                ? `${outstandingRecords} ${outstandingRecords === 1 ? "item" : "items"}`
                : "None"}
            </span>
          </div>
          <div className="text-foreground flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 sm:col-span-2 xl:col-span-1">
            <span className="text-muted-foreground">Balance:</span>
            <span className="font-semibold">{formatCurrency(balance)}</span>
            {balanceConfirmationMeta ? (
              <TooltipWrapper message={balanceConfirmationMeta}>
                {balanceChip}
              </TooltipWrapper>
            ) : (
              balanceChip
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProviderCard(props: ProviderCardProps) {
  return (
    <ErrorBoundary
      fallback={
        <div className="p-4">
          <ServerErrorFallbackCard message="Unable to load treatment provider details." />
        </div>
      }
    >
      <ProviderCardDangerous {...props} />
    </ErrorBoundary>
  );
}
